import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { resetPassword } from "../../api/auth";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();

  const [password, setPasswordValue] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!token) {
      setError("Invalid reset link. Please request a new one.");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (!/[A-Z]/.test(password)) {
      setError("Password must contain at least one uppercase letter.");
      return;
    }
    if (!/\d/.test(password)) {
      setError("Password must contain at least one number.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || "Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-surface-900 flex items-center justify-center p-6">
        <div className="w-full max-w-sm text-center">
          <h1 className="text-h1 text-text-primary mb-4">Invalid Link</h1>
          <p className="text-body text-text-secondary mb-6">
            This reset link is invalid or has expired.
          </p>
          <button onClick={() => navigate("/forgot-password")} className="btn-primary px-6 py-2">
            Request New Link
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-surface-900 flex items-center justify-center p-6">
        <div className="w-full max-w-sm text-center">
          <div className="mb-6 p-3 rounded-lg bg-quantum-500/10 border border-quantum-500/30">
            <p className="text-body text-quantum-400">Password reset successfully!</p>
          </div>
          <p className="text-body text-text-secondary mb-6">
            You can now sign in with your new password.
          </p>
          <button onClick={() => navigate("/login")} className="btn-primary px-6 py-2">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-900 flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src="/quantum-small.png" alt="TotalView" className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-h1 text-text-primary mb-2">Reset Password</h1>
          <p className="text-body text-text-secondary">
            Choose a new password for your account.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-health-500/10 border border-health-500/30">
            <p className="text-body-sm text-health-400 text-center">{error}</p>
          </div>
        )}

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="password" className="input-label">New Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPasswordValue(e.target.value)}
              placeholder="Create a new password"
              className="input"
              disabled={loading}
              autoComplete="new-password"
            />
            <p className="mt-1.5 text-caption text-text-muted">
              At least 8 characters, one uppercase letter, and one number.
            </p>
          </div>
          <div>
            <label htmlFor="confirm-password" className="input-label">Confirm Password</label>
            <input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              className="input"
              disabled={loading}
              autoComplete="new-password"
            />
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full py-3">
            {loading ? "Resetting..." : "Reset Password"}
          </button>
        </form>
      </div>
    </div>
  );
}
